/**
 * Wither runtime glue (one instance in main.ts): the skull projectiles (sim + renderer), the
 * summoning check after a block is placed, the wither's block smashing with drops, its spawn /
 * shoot sounds and the "Wither" boss bar (src/ui/bossBar.ts, stacked under the dragon's bar when
 * both are on screen).
 *
 *   const witherRt = new WitherRuntime({...});
 *   after a block is placed:  witherRt.onPlace(x, y, z, blockId)
 *   gameTick: witherRt.tick();  every frame: witherRt.frame(alpha);  dimension switch: witherRt.clear()
 */

import * as THREE from 'three';
import type { ChunkManager } from '../world/manager';
import type { EntityManager } from '../entities';
import type { EntityHost, PlayerTarget } from '../entities/host';
import { Wither, WITHER_INVUL, witherHooks } from '../entities/boss/wither';
import { WitherSkulls } from '../entities/boss/skulls';
import { WITHER_SKULL_ID } from '../blocks';
import { WitherSkullRenderer } from '../render/witherSkulls';
import type { EnvUniforms } from '../render/env';
import type { GuiStage } from '../ui/kit';
import { BossBar } from '../ui/bossBar';
import { dropsFor } from './mining';
import { trySummonWither } from './witherSummon';
import type { Stack } from './types';

export interface WitherRuntimeHost {
  chunks: ChunkManager;
  entities: EntityManager;
  env: EnvUniforms;
  scene: THREE.Object3D;
  stage: GuiStage;
  /** Block explosion + area damage (the same wiring as the ghast fireball's). */
  explode(x: number, y: number, z: number, power: number): void;
  dropItem(s: Stack, x: number, y: number, z: number): void;
  /** A skull hit this player: give them the effect (local player only; remote bodies are theirs to apply). */
  playerEffect(p: PlayerTarget, id: number, amp: number, dur: number): void;
  difficulty(): ReturnType<NonNullable<EntityHost['difficulty']>>;
  sfx: { play(id: string, pos?: { x: number; y: number; z: number }): void };
}

/** BossBar rows: vanilla stacks them 19 GUI px apart (bar 5 + name 9 + gaps). */
const BAR_TOP = 12, BAR_STACKED = 31;

export class WitherRuntime {
  readonly skulls = new WitherSkulls();
  private readonly renderer: WitherSkullRenderer;
  private readonly bar: BossBar;
  private readonly invul = new Map<number, number>();

  constructor(private readonly h: WitherRuntimeHost) {
    this.renderer = new WitherSkullRenderer(h.env);
    h.scene.add(this.renderer.group);
    this.bar = new BossBar(h.stage.content, BAR_TOP);
    witherHooks.shoot = (blue, x, y, z, dx, dy, dz, owner) => { this.skulls.spawn(blue, x, y, z, dx, dy, dz, owner); };
    witherHooks.explode = (x, y, z, power) => h.explode(x, y, z, power);
    witherHooks.breakBlock = (x, y, z) => this.breakBlock(x, y, z);
    this.skulls.onShoot = (s) => h.sfx.play('entity.wither.shoot', s);
  }

  /** A wither smashing a block: drops as if mined by hand. */
  private breakBlock(x: number, y: number, z: number): boolean {
    const c = this.h.chunks;
    if (!c.canEdit(x, y, z)) return false;
    const id = c.world.getBlock(x, y, z);
    for (const s of dropsFor(id, 0, Math.random)) this.h.dropItem(s, x + 0.5, y + 0.5, z + 0.5);
    c.setBlock(x, y, z, 0);
    return true;
  }

  /** The player placed block `id` at (x, y, z): a third skull on a soul sand T summons the wither. */
  onPlace(x: number, y: number, z: number, id: number): boolean {
    if (id !== WITHER_SKULL_ID) return false;
    const h = this.h;
    const w = trySummonWither({
      getBlock: (a, b, c) => h.chunks.world.getBlock(a, b, c),
      setBlock: (a, b, c, v) => { h.chunks.setBlock(a, b, c, v); },
      spawn: (t, a, b, c) => h.entities.spawnMob(t, a, b, c),
      difficulty: () => h.difficulty(),
    }, x, y, z);
    if (!w) return false;
    this.invul.set(w.id, WITHER_INVUL);
    return true;
  }

  clear(): void { this.skulls.clear(); this.invul.clear(); }

  tick(): void {
    const h = this.h;
    this.skulls.tick({
      world: h.chunks.world,
      players: () => h.entities.allTargets(),
      mobs: () => h.entities.mobs,
      hurtMob: (m, a, fx, fz) => { h.entities.attackFromMob(m, a, fx, fz); },
      explode: (x, y, z, power) => h.explode(x, y, z, power),
      breakBlock: (x, y, z) => (h.chunks.canEdit(x, y, z) ? (h.chunks.setBlock(x, y, z, 0), true) : false),
      playerEffect: (p, id, amp, dur) => h.playerEffect(p, id, amp, dur),
      difficulty: () => h.difficulty(),
    });
    // the end of the spawn sequence: the roar (the power-7 blast itself runs in Wither.ai)
    for (const m of h.entities.mobs) {
      if (!(m instanceof Wither)) continue;
      const was = this.invul.get(m.id) ?? 0;
      if (was > 0 && m.invul === 0 && m.alive) h.sfx.play('entity.wither.spawn', { x: m.x, y: m.y + 2, z: m.z });
      this.invul.set(m.id, m.invul);
    }
  }

  frame(alpha: number): void {
    const h = this.h;
    this.renderer.update(this.skulls.list, alpha);
    // the bar follows the wither nearest to the camera; the dragon's bar (the End) keeps the top row
    let best: Wither | null = null, bd = Infinity;
    const p = h.entities.allTargets()[0];
    for (const m of h.entities.mobs) {
      if (!(m instanceof Wither) || !m.alive) continue;
      const d = p ? (m.x - p.x) ** 2 + (m.z - p.z) ** 2 : 0;
      if (d < bd) { bd = d; best = m; }
    }
    this.bar.setTop(h.chunks.dimension === 'end' ? BAR_STACKED : BAR_TOP);
    this.bar.update(best && bd < 128 * 128
      ? { name: 'Wither', fraction: best.invul > 0 ? 1 - best.invul / WITHER_INVUL : Math.max(0, Math.min(1, best.health / best.maxHealth)) }
      : null, h.stage.width);
  }
}
